import { Box, Typography } from "@mui/material";
import { type FC, useState } from "react";
import { useTranslation } from "react-i18next";
import { FEATURE_FLAGS } from "../../contexts/FeatureFlagsProvider";
import { useFeatureFlag } from "../../hooks/useOpenFeatureFlag";
import { FONT_SIZE, INK } from "../../theme/tokens";
import { parseDelayHours } from "../../utils/agencyPostDelay";
import type { OpenShiftRequest } from "../../utils/models";
import { DelayHoursField } from "../UIToolkit/Inputs/DelayHoursField";
import { useUpdateAgencyPostDelay } from "./hooks/useUpdateAgencyPostDelay";

type Props = {
	request: OpenShiftRequest;
};

/**
 * Re-times a pending agency post from the edit-hours popover. Saves when the input loses focus.
 *
 * Renders nothing unless the shift is waiting to be sent — one already at the agency, or never
 * headed there, has no delay to change. No checkbox on purpose: the only choice left is how long.
 */
export const AgencyPostDelayField: FC<Props> = ({ request }) => {
	const { t } = useTranslation();
	const delayedAgencyPostingEnabled = useFeatureFlag(FEATURE_FLAGS.DelayedAgencyPostingEnabled);
	const savedHours = request.agency_post_delay_hours;
	const [hours, setHours] = useState<string>(String(savedHours ?? ""));
	const [syncedTo, setSyncedTo] = useState(savedHours);
	const { mutate: updateDelay, isPending } = useUpdateAgencyPostDelay();

	// A refresh elsewhere can change the saved delay; follow it rather than keep a stale edit.
	if (savedHours !== syncedTo) {
		setSyncedTo(savedHours);
		setHours(String(savedHours ?? ""));
	}

	if (!delayedAgencyPostingEnabled || savedHours === null || savedHours === undefined) return null;

	const commit = () => {
		const parsed = parseDelayHours(hours);
		// Revert rather than persist something the backend would reject.
		if (parsed === null) {
			setHours(String(savedHours));
			return;
		}
		// Put the saved value back on failure, so the field never shows a delay that wasn't stored.
		if (parsed !== savedHours) {
			updateDelay({ requestId: request.id, agencyPostDelayHours: parsed }, { onError: () => setHours(String(savedHours)) });
		}
	};

	return (
		<Box sx={{ mt: 2, display: "flex", flexDirection: "column", gap: 0.75 }}>
			<Typography component="span" sx={{ fontSize: FONT_SIZE.SECONDARY, fontWeight: 600, color: INK.SECONDARY }}>
				{t("post.delay")}
			</Typography>
			<Box onBlur={commit}>
				<DelayHoursField
					value={hours}
					onChange={setHours}
					suffixLabel={t("hours.before.shift.start")}
					disabled={isPending}
					dataTestId="request-agency-post-delay"
				/>
			</Box>
		</Box>
	);
};
